import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { useAgencyStore, useRegionStore } from 'Application/store';
import { IntermediaryTypes } from 'Application/models';
import { ClearIcon } from '@mui/x-date-pickers';
import { ThemeMode } from 'config';
import MuiPhoneNumber from 'mui-phone-number';
import { validatePhoneNumber } from 'Application/utils';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Agency name is required'),
    emailAddress: Yup.string().email('Invalid email').required('Agency Email is required'),
    phoneNumber: Yup.string().required('Phone Number is required'),
    agentNumber: Yup.string().required('Agent Number is required'),
    address: Yup.string().required('Address is required'),
    intermediaryTypes: Yup.string().required('Intermediary type is required'),
    branchCode: Yup.string().required('Branch Code is required')
});

interface AgencyDetailsProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    profileData: any; // Add this prop to pass the agency ID
}

export const EditAgencyProfile: FC<AgencyDetailsProps> = ({ isOpen, setIsOpen, profileData }) => {
    const { editAgency, isLoading } = useAgencyStore();
    const agencyId = profileData.id;

    const { getRegions, regions } = useRegionStore();

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    const agencyRegionName = profileData.regionName;
    const agencyRegionDetails = regions?.find((region) => region.name === agencyRegionName);
    const agencyCountryAbbreviation = agencyRegionDetails?.countryAbbreviation.toLowerCase();

    const [formData, setFormData] = useState({
        name: profileData.name,
        address: profileData.address,
        emailAddress: profileData.emailAddress,
        phoneNumber: profileData.phoneNumber,
        agentNumber: profileData.agentNumber,
        branchCode: profileData.branchCode,
        intermediaryType: profileData.intermediaryType,
        allowLocalTravel: profileData.intermediaryLocalTravelStatus ? 'Yes' : 'No'
    });

    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: profileData.name,
                address: profileData.address,
                emailAddress: profileData.emailAddress,
                phoneNumber: profileData.phoneNumber,
                agentNumber: profileData.agentNumber,
                branchCode: profileData.branchCode,
                intermediaryType: profileData.intermediaryType,
                allowLocalTravel: profileData.intermediaryLocalTravelStatus ? 'Yes' : 'No'
            });
        }
    }, [isOpen, profileData]);

    useEffect(() => {
        getRegions();
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSelectChange = (event: SelectChangeEvent) => {
        const { value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            intermediaryType: value
        }));
    };

    const handleChangeAllowLocalTravel = (event: SelectChangeEvent) => {
        const { value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            allowLocalTravel: value
        }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (validatePhoneNumber(formData.phoneNumber) === false) {
            toast.error('Please enter a valid phone number');
            return;
        }

        const edittedAgencyData = {
            name: formData.name,
            address: formData.address,
            emailAddress: formData.emailAddress,
            phoneNumber: formData.phoneNumber,
            agentNumber: formData.agentNumber,
            branchCode: formData.branchCode,
            intermediaryType: formData.intermediaryType,
            isLocalTravelAllowed: formData.allowLocalTravel === 'Yes' ? true : false
        };

        try {
            await editAgency(edittedAgencyData, agencyId);
            toast.success('Agency profile updated successfully');
            setIsOpen(false);
        } catch (error: any) {
            toast.error(`Failed to update agency profile: ${error.message}`);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Edit Intermediary Profile</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Name
                        </Typography>
                        <TextField
                            placeholder="Enter Name"
                            required
                            name="name"
                            variant="outlined"
                            onChange={handleInputChange}
                            value={formData.name}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Email
                        </Typography>
                        <TextField
                            placeholder="Enter Email"
                            required
                            name="emailAddress"
                            variant="outlined"
                            onChange={handleInputChange}
                            type="email"
                            value={formData.emailAddress}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Phone Number
                        </Typography>
                        <MuiPhoneNumber
                            required
                            id="phoneNumber"
                            name="phoneNumber"
                            fullWidth
                            defaultCountry={agencyCountryAbbreviation}
                            variant="outlined"
                            onChange={(value) => {
                                setFormData({ ...formData, phoneNumber: value });
                            }}
                            value={formData.phoneNumber}
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Agent Number
                        </Typography>
                        <TextField
                            placeholder="Enter Agent Number"
                            required
                            name="agentNumber"
                            variant="outlined"
                            onChange={handleInputChange}
                            type="number"
                            value={formData.agentNumber}
                            // inputProps={{ min: 0 }}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Address
                        </Typography>
                        <TextField
                            placeholder="Enter Address"
                            required
                            name="address"
                            variant="outlined"
                            onChange={handleInputChange}
                            type="text"
                            value={formData.address}
                            // inputProps={{ min: 0 }}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Branch Code
                        </Typography>
                        <TextField
                            placeholder="Enter Branch Code"
                            required
                            name="branchCode"
                            variant="outlined"
                            onChange={handleInputChange}
                            type="number"
                            value={formData.branchCode}
                            // inputProps={{ min: 0 }}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Intermediary Type
                        </Typography>
                        <Select value={formData.intermediaryType} onChange={handleSelectChange} fullWidth>
                            <MenuItem value={IntermediaryTypes.TRAVEL_AGENCY}>Travel Agency</MenuItem>
                            <MenuItem value={IntermediaryTypes.INSURANCE_BROKER}>Insurance Broker</MenuItem>
                            <MenuItem value={IntermediaryTypes.FINANCIAL_ADVISOR}>Financial Advisor</MenuItem>
                        </Select>
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Allow local travel
                        </Typography>
                        <Select value={formData.allowLocalTravel} onChange={handleChangeAllowLocalTravel} fullWidth>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                        </Select>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};
